import { cn } from '@/helpers/ui-utils';
import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';

const alertVariants = cva(
  'relative w-full rounded-xs border border-2 border-solid p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground',
  {
    variants: {
      variant: {
        default: 'border-foreground bg-muted text-foreground',
        primary: 'border-primary bg-primary text-primary-foreground',
        'primary-muted': 'border-primary bg-primary/10 text-primary',
        secondary: 'border-secondary bg-secondary text-secondary-foreground',
        'secondary-muted': 'border-secondary bg-secondary/10 text-secondary',
        destructive:
          'border-destructive bg-destructive text-destructive-foreground [&>svg]:text-destructive-foreground',
        'destructive-muted':
          'border-destructive bg-destructive/10 text-destructive [&>svg]:text-destructive-foreground',
        positive:
          'border-positive bg-positive text-positive-foreground [&>svg]:text-positive-foreground',
        'positive-muted':
          'border-positive bg-positive/10 text-positive-hover [&>svg]:text-positive-foreground',
      },
      rounding: {
        none: 'rounded-none',
        sm: 'rounded-sm',
        md: 'rounded-md',
        lg: 'rounded-lg',
        full: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      rounding: 'md',
    },
  },
);

type AlertCVAProps = {
  /**
   * Visual style variant of the alert. Determines color scheme and emphasis level for different alert types.
   * - `default` - Standard gray alert with muted background
   * - `primary` - Primary color alert with solid background
   * - `primary-muted` - Primary color alert with light background
   * - `secondary` - Secondary color alert with solid background
   * - `secondary-muted` - Secondary color alert with light background
   * - `destructive` - Red alert for errors or dangerous situations
   * - `destructive-muted` - Light red alert for error contexts
   * - `positive` - Green alert for success states
   * - `positive-muted` - Light green alert for success contexts
   * @default 'default'
   */
  variant?: VariantProps<typeof alertVariants>['variant'];

  /**
   * Border radius variant. Controls corner rounding from sharp edges to fully rounded.
   * - `none` - No rounding (sharp corners)
   * - `sm` - Small rounding
   * - `md` - Medium rounding
   * - `lg` - Large rounding
   * - `full` - Fully rounded corners
   * @default 'md'
   */
  rounding?: VariantProps<typeof alertVariants>['rounding'];
};

/**
 * Display important messages, notifications, and status updates with contextual styling and optional icons.
 *
 * @param props - Alert properties. See {@link AlertCVAProps} for detailed prop documentation including inherited CVA variants.
 * @param ref - Forwarded ref to the alert element
 * @returns Rendered alert component
 *
 * @example
 * ```tsx
 * // Basic alert
 * <Alert>
 *   <AlertTitle>Information</AlertTitle>
 *   <AlertDescription>This is a basic alert message.</AlertDescription>
 * </Alert>
 *
 * // Error alert with icon
 * <Alert variant="destructive">
 *   <AlertCircle className="h-4 w-4" />
 *   <AlertTitle>Error</AlertTitle>
 *   <AlertDescription>Something went wrong.</AlertDescription>
 * </Alert>
 *
 * // Success alert
 * <Alert variant="positive">
 *   <CheckCircle className="h-4 w-4" />
 *   <AlertTitle>Success</AlertTitle>
 *   <AlertDescription>Operation completed successfully.</AlertDescription>
 * </Alert>
 * ```
 */
const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & AlertCVAProps
>(({ className, variant, rounding, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant, rounding }), className)}
    {...props}
  />
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn(
      'm-0 mb-3 text-md font-medium leading-none tracking-tight text-inherit',
      className,
    )}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-foreground [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertDescription, AlertTitle };
